import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { permissionsApi } from '../api/endpoints';
import { AppModule, PermissionLevel, UserPermission } from '../types';

interface PermissionsContextType {
  permissions: Record<AppModule, PermissionLevel>;
  loading: boolean;
  hasRead: (module: AppModule) => boolean;
  hasWrite: (module: AppModule) => boolean;
  refreshPermissions: () => Promise<void>;
}

const defaultPermissions: Record<AppModule, PermissionLevel> = {
  Dashboard: 'None',
  PurchaseOrders: 'None',
  Shipments: 'None',
  Invoices: 'None',
  Products: 'None',
  Inventory: 'None',
  Users: 'None',
  Sync: 'None',
};

const PermissionsContext = createContext<PermissionsContextType>({
  permissions: defaultPermissions,
  loading: true,
  hasRead: () => false,
  hasWrite: () => false,
  refreshPermissions: async () => {},
});

export const usePermissions = () => useContext(PermissionsContext);

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const [permissions, setPermissions] = useState<Record<AppModule, PermissionLevel>>(defaultPermissions);
  const [loading, setLoading] = useState(true);

  const refreshPermissions = useCallback(async () => {
    try {
      const res = await permissionsApi.me();
      const perms = { ...defaultPermissions };
      (res.data as UserPermission[]).forEach((p) => {
        perms[p.module] = p.level;
      });
      setPermissions(perms);
    } catch {
      // Keep defaults on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      refreshPermissions();
    }
  }, [isAuthenticated, refreshPermissions]);

  const hasRead = useCallback(
    (module: AppModule) => {
      const level = permissions[module];
      return level === 'Read' || level === 'Write';
    },
    [permissions]
  );

  const hasWrite = useCallback(
    (module: AppModule) => {
      return permissions[module] === 'Write';
    },
    [permissions]
  );

  return (
    <PermissionsContext.Provider value={{ permissions, loading, hasRead, hasWrite, refreshPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
};
